import { Controller } from "@hotwired/stimulus"

// SEND TO TEAM, the small window (owner directive 2026-10-01,
// Send_To_TEAM_Plan.md). The bookmarks-bar button opens this on the
// article she is reading and posts it the page's tags and lead image. This
// passes them to the press clip editor open in another TEAM tab over a
// BroadcastChannel (same site, so every TEAM tab in her browser hears it),
// and tells her what happened. Every word she reads after clicking the
// button is here, not in the button, because the button can never be
// updated once it is on her bookmarks bar.
//
// TRUST. Anything may post to this window, so it listens only to the
// window that opened it, and stamps what it passes on with that window's
// real origin, which the browser vouches for. The article's own fields
// are the page's word about itself. The editor takes an article only from
// the host of the link it is working on, so a hostile page can only ever
// fill a clip about itself, which is the same trust as reading its tags.
const VERSION = 1
const ANSWER_WAIT_MS = 1500
const ARTICLE_WAIT_MS = 10000
const CLOSE_AFTER_MS = 2000
const MAX_HEAD = 256 * 1024
const MAX_IMAGE = 6 * 1024 * 1024
const JPEG = "data:image/jpeg;base64,"

// Owner-approved verbatim, 2026-10-01 ("Wording approved").
const SAID = {
  sending: "Sending this article to TEAM...",
  taken: "Sent to TEAM. Switch to your TEAM tab to finish the clip.",
  none: "TEAM did not find a press clip waiting for this article. In TEAM, click Add Press Clip, paste this article's link into Source URL, then click Send to TEAM again.",
  otherLink: (link) => `The clip open in TEAM is for a different article (${link}). Close it, paste this article's link into Source URL, then click Send to TEAM again.`,
  editingSaved: "TEAM has this article's saved clip open in Edit Clipping. Close the editor with the X, then click Send to TEAM again.",
  noArticle: "The article did not arrive. Click Send to TEAM on the article again.",
  outOfDate: "This Send to TEAM button is out of date. Drag the new one below onto your bookmarks bar, then delete the old one."
}

export default class extends Controller {
  static targets = ["message", "install"]

  // Opened by hand there is no opener, and the page as rendered is the
  // install page. Opened by the button, it asks for the article.
  connect() {
    if (!window.opener) return

    this.installTarget.classList.add("hidden")
    this.say(SAID.sending)
    this._onMessage = (event) => this.receive(event)
    window.addEventListener("message", this._onMessage)
    // No article yet, so nothing to protect: any origin may hear "ready".
    window.opener.postMessage({ type: "send-to-team-ready" }, "*")
    // The button sends within five seconds of "ready" (it gives up on a slow
    // picture); anything later means the article is not coming.
    this._noArticle = setTimeout(() => this.say(SAID.noArticle), ARTICLE_WAIT_MS)
  }

  disconnect() {
    if (this._onMessage) window.removeEventListener("message", this._onMessage)
    clearTimeout(this._wait)
    clearTimeout(this._noArticle)
    if (this._channel) this._channel.close()
  }

  receive(event) {
    if (event.source !== window.opener || !event.data || event.data.type !== "send-to-team") return
    if (!/^https?:\/\//.test(event.origin)) return

    clearTimeout(this._noArticle)
    window.removeEventListener("message", this._onMessage)   // one article per window
    const article = this.clean(event.data.article, event.origin)
    if (!article) {
      this.installTarget.classList.remove("hidden")
      return this.say(SAID.outOfDate)
    }
    this.relay(article)
  }

  // Only what the editor reads, each field typed and capped. The image is
  // the button's own JPEG or nothing.
  clean(raw, origin) {
    if (!raw || raw.v !== VERSION) return null

    const http = (value) => {
      try {
        const url = new URL(String(value))
        return /^https?:$/.test(url.protocol) ? url.href : ""
      } catch (error) {
        return ""
      }
    }
    const image = typeof raw.image === "string" && raw.image.startsWith(JPEG) && raw.image.length <= MAX_IMAGE ? raw.image : ""
    return {
      origin,
      url: http(raw.url),
      canonical: http(raw.canonical),
      head: typeof raw.head === "string" ? raw.head.slice(0, MAX_HEAD) : "",
      imageUrl: http(raw.imageUrl),
      image
    }
  }

  // Every TEAM tab hears it; the one working on this article answers
  // "taken" at once and fills itself afterwards. Other answers say why a
  // tab could not take it. Silence means no TEAM tab is waiting for it.
  relay(article) {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    const answers = []
    this._channel = new BroadcastChannel("send-to-team")
    this._channel.onmessage = ({ data }) => {
      if (!data || data.type !== "answer" || data.id !== id) return
      answers.push(data)
      if (data.kind === "taken") this.answered(answers)
    }
    this._channel.postMessage({ type: "article", id, article })
    this._wait = setTimeout(() => this.answered(answers), ANSWER_WAIT_MS)
  }

  answered(answers) {
    if (this._answered) return   // two TEAM tabs on the same article both say "taken"
    this._answered = true
    clearTimeout(this._wait)
    if (this._channel) this._channel.close()
    this._channel = null

    const find = (kind) => answers.find((answer) => answer.kind === kind)
    if (find("taken")) {
      this.say(SAID.taken)
      setTimeout(() => window.close(), CLOSE_AFTER_MS)
    } else if (find("editing-saved")) {
      this.say(SAID.editingSaved)
    } else if (find("other-link")) {
      this.say(SAID.otherLink(find("other-link").link))
    } else {
      this.say(SAID.none)
    }
  }

  // Only "taken" closes the window by itself. A message she has to act on
  // stays up until she closes it with the Close bar at the top.
  say(text) {
    this.messageTarget.textContent = text
  }
}
